/**
 * The MEMBER-facing loop INVOKE rpc (Loop Studio): a person — or their agent,
 * through the MCP — starts a walk of one of their loops.
 *
 * `loops.invoke` forwards to the harness's own `harness.invoke`
 * (`rpc/harness-invoke.rpc.ts`). {@link LoopInvokeResponse} has the same fields
 * as `HarnessInvokeResponse`; it is declared here rather than imported because
 * that module imports `@fm/cluster`, while this one is imported by the browser
 * and the MCP binary.
 *
 * Contracts only — the handler lives in `loop-invoke.router.ts`.
 *
 * `customerId` on the payload is a SELECTOR among the caller's own memberships,
 * never an authority — `resolveActorCustomer` (`loop-scope.ts`) is the only
 * thing that turns a request into a customer scope.
 *
 * @since 1.0.0
 */
import { Rpc, RpcGroup } from "@effect/rpc";
import { Schema } from "effect";

import { DatabaseError, Forbidden, RecordNotFoundError, Unauthorized, ValidationError } from "../errors.ts";
import { LoopActorMiddleware } from "../loop-actor.tags.ts";

/**
 * Start one invocation of a loop with explicit inputs. `triggerKey` makes the
 * call idempotent: the same key never starts a second walk.
 *
 * @since 1.0.0
 */
export const LoopInvokeInput = Schema.Struct({
  loopId: Schema.String,
  inputs: Schema.Record({ key: Schema.String, value: Schema.Unknown }),
  triggerKey: Schema.optional(Schema.String),
  customerId: Schema.optional(Schema.String),
});
export type LoopInvokeInput = Schema.Schema.Type<typeof LoopInvokeInput>;

/**
 * The invocation the harness created — field for field `HarnessInvokeResponse`.
 *
 * @since 1.0.0
 */
export const LoopInvokeResponse = Schema.Struct({
  invocationId: Schema.String,
  loopId: Schema.String,
  loopVersion: Schema.Number,
  /** `null` when the invocation row exists but the walk's dispatch failed. */
  executionId: Schema.NullOr(Schema.String),
});
export type LoopInvokeResponse = Schema.Schema.Type<typeof LoopInvokeResponse>;

/**
 * The harness could not be reached, or answered with something other than an
 * invocation or a typed refusal (a transport failure). Nothing was invoked
 * that the caller can see; retrying with the same `triggerKey` is safe.
 *
 * @since 1.0.0
 */
export class LoopInvokeUnavailable extends Schema.TaggedError<LoopInvokeUnavailable>()(
  "LoopInvokeUnavailable",
  {
    message: Schema.String,
  },
) {}

/**
 * Stop one walk the caller's customer owns. `customerId` is a selector, as on
 * {@link LoopInvokeInput}; without one the owning membership is found by the
 * invocation id.
 *
 * @since 1.0.0
 */
export const LoopCancelInvocationInput = Schema.Struct({
  invocationId: Schema.String,
  customerId: Schema.optional(Schema.String),
});
export type LoopCancelInvocationInput = Schema.Schema.Type<typeof LoopCancelInvocationInput>;

/**
 * What the harness did to the walk. `interrupted`: its live execution was
 * stopped. `canceled`: it had no execution yet and was concluded. `canceled_dead`:
 * its execution was already gone and the row was concluded. Declared here
 * rather than imported from `HarnessCancelResponse` for the same reason as
 * {@link LoopInvokeResponse}; `loopId` is added so a caller can link the walk.
 *
 * @since 1.0.0
 */
export const LoopCancelInvocationResponse = Schema.Struct({
  invocationId: Schema.String,
  loopId: Schema.String,
  previousStatus: Schema.String,
  action: Schema.Literal("interrupted", "canceled", "canceled_dead"),
});
export type LoopCancelInvocationResponse = Schema.Schema.Type<typeof LoopCancelInvocationResponse>;

const LoopInvokeErrors = Schema.Union(
  Unauthorized,
  Forbidden,
  RecordNotFoundError,
  ValidationError,
  DatabaseError,
  LoopInvokeUnavailable,
);

/**
 * The member-facing walk group: start a walk, stop a walk. Every rpc carries
 * {@link LoopActorMiddleware}.
 *
 * @since 1.0.0
 */
export class LoopInvokeRpcs extends RpcGroup.make(
  Rpc.make("loops.invoke", {
    payload: LoopInvokeInput,
    success: LoopInvokeResponse,
    error: LoopInvokeErrors,
  }).middleware(LoopActorMiddleware),
  Rpc.make("loops.cancelInvocation", {
    payload: LoopCancelInvocationInput,
    success: LoopCancelInvocationResponse,
    error: LoopInvokeErrors,
  }).middleware(LoopActorMiddleware),
) {}
